import type { NativeContentSegment } from './nativeTurn'

export interface NativeHistoryRecord {
  id?: number
  role: 'user' | 'assistant'
  content: string
  thinking?: string | null
  kohakuThinking?: string | null
  segments?: NativeContentSegment[] | null
  turnId?: string | null
  ts?: string
}

export interface NativeDiagnosticRecord {
  id?: number
  turn_id: string
  ts: string
  category: string
  message?: string | null
  provider_message?: string | null
  visible_text?: string | null
  api_error_status?: number | null
  result_subtype?: string | null
  result_is_error?: boolean | null
  origin?: string | null
}

export type NativeTimelineRecord =
  | { kind: 'message'; value: NativeHistoryRecord; order: number }
  | { kind: 'diagnostic'; value: NativeDiagnosticRecord; order: number }

function timestampValue(value?: string | null): number | null {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function mergeNativeHistoryDiagnostics(
  messages: NativeHistoryRecord[],
  diagnostics: NativeDiagnosticRecord[],
): NativeTimelineRecord[] {
  const records: NativeTimelineRecord[] = []
  messages.forEach((value, order) => records.push({ kind: 'message', value, order }))
  diagnostics.forEach((value, order) => records.push({ kind: 'diagnostic', value, order: messages.length + order }))

  return records.sort((a, b) => {
    const aTs = timestampValue(a.kind === 'message' ? a.value.ts : a.value.ts)
    const bTs = timestampValue(b.kind === 'message' ? b.value.ts : b.value.ts)
    if (aTs !== null && bTs !== null && aTs !== bTs) return aTs - bTs
    if (aTs !== null && bTs === null) return -1
    if (aTs === null && bTs !== null) return 1

    // When a failed turn has an identical coarse timestamp, keep the diagnostic
    // after its human message rather than making the failure look causal-inverse.
    if (a.kind === 'message' && b.kind === 'diagnostic') {
      if (a.value.turnId && a.value.turnId === b.value.turn_id) return -1
    }
    if (a.kind === 'diagnostic' && b.kind === 'message') {
      if (b.value.turnId && b.value.turnId === a.value.turn_id) return 1
    }
    return a.order - b.order
  })
}

export function nativeDiagnosticSummary(diagnostic: NativeDiagnosticRecord): string {
  if (diagnostic.api_error_status) return `API error · ${diagnostic.api_error_status}`
  if (diagnostic.category === 'provider_error') return 'Provider error'
  if (diagnostic.category === 'cancelled') return 'Turn cancelled'
  return diagnostic.category ? diagnostic.category.replaceAll('_', ' ') : 'Turn failed'
}
